import React from 'react';
import { useTranslations } from 'use-intl';
import { Typography } from '@/UIlibrary/Typography/Typography';
import Button from '@/UIlibrary/Button/Button';
import { Table, ColumnDefinition } from '@/UIlibrary/Table/Table';
import * as XLSX from 'xlsx';
import { GRAY_COLORS } from '@/UIlibrary/globalTheme';
import { DaysPerSimulationPeriod, Simulation } from '@/graphql/__generated__/van-credits';
import { Maybe } from 'graphql/jsutils/Maybe';
import { Printer } from 'react-feather';

interface SimulationTableProps {
	loading: boolean;
	data: Maybe<Simulation> | undefined;
	inCreditCover?: boolean;
}

const SimulationTable: React.FC<SimulationTableProps> = ({
	loading,
	data,
	inCreditCover = false
}) => {
	const t = useTranslations('VanCreditRequestSimulation');

	const columns: ColumnDefinition[] = [
		{ header: t('PaymentNumber'), key: 'paymentNumber' },
		{ header: t('DueDate'), key: 'dueDate' },
		{ header: t('InitialBalance'), key: 'initialBalance' },
		{ header: t('CreditPayment'), key: 'creditPayment' },
		{ header: t('PrincipalPayment'), key: 'principalPayment' },
		{ header: t('InterestPayment'), key: 'interestPayment' },
		{ header: t('OutstandingBalance'), key: 'outstandingBalance' },
		{ header: t('InterestTax'), key: 'interestTax', cellRenderer: (value) => value || '-' },
		{ header: t('GPSAmount'), key: 'gpsAmount' },
		{ header: t('GPSTax'), key: 'gpsTax' },
		{ header: t('InsuranceAmount'), key: 'insuranceAmount' },
		{ header: t('InsuranceTax'), key: 'insuranceTax' },
		{ header: t('Total'), key: 'totalPayment' }
	];

	const handleExport = (data: Simulation, fileName: string) => {
		const { installments, configuration } = data;
		const fileType =
			'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
		const fileExtension = '.xlsx';

		const wb = XLSX.utils.book_new();

		const detailsData = [
			[t('LoanDetails')],
			[t('GenerationDate'), new Date().toLocaleDateString()],
			[t('NumberOfPeriods'), data.numberOfPeriods],
			[t('DaysPerPeriod'), mapDaysPeriod(data.daysPerPeriod ?? '')],
			[t('PaymentCapacity'), data.paymentCapacity],
			[t('LastPaymentAmount'), data.lastPaymentAmount],
			[t('AmountRequested'), configuration?.amountRequested],
			[], // Empty row for spacing
			[t('AmortizationTable')],
			[
				t('PaymentNumber'),
				t('DueDate'),
				t('InitialBalance'),
				t('CreditPayment'),
				t('PrincipalPayment'),
				t('InterestPayment'),
				t('OutstandingBalance'),
				t('InterestTax'),
				t('GPSAmount'),
				t('GPSTax'),
				t('InsuranceAmount'),
				t('InsuranceTax'),
				t('Total')
			]
		];

		installments?.forEach((payment) => {
			if (!payment) return;
			detailsData.push([
				payment.paymentNumber,
				payment.dueDate,
				payment.initialBalance,
				payment.creditPayment,
				payment.principalPayment,
				payment.interestPayment || 'N/A',
				payment.outstandingBalance || 'N/A',
				payment.interestTax || 'N/A',
				payment.gpsAmount || 'N/A',
				payment.gpsTax || 'N/A',
				payment.insuranceAmount || 'N/A',
				payment.insuranceTax || 'N/A',
				payment.totalPayment || 'N/A'
			]);
		});

		// Create worksheet
		const ws = XLSX.utils.aoa_to_sheet(detailsData);

		// Set column widths
		const colWidths = [
			{ wch: 25 },
			{ wch: 15 },
			{ wch: 15 },
			{ wch: 15 },
			{ wch: 15 },
			{ wch: 15 },
			{ wch: 15 },
			{ wch: 15 },
			{ wch: 15 },
			{ wch: 15 },
			{ wch: 15 },
			{ wch: 15 },
			{ wch: 15 },
			{ wch: 15 }
		];
		ws['!cols'] = colWidths;

		// Add worksheet to workbook
		XLSX.utils.book_append_sheet(wb, ws, t('AmortizationTable'));

		const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
		const data_blob = new Blob([excelBuffer], { type: fileType });

		const link = document.createElement('a');
		const url = window.URL.createObjectURL(data_blob);
		link.href = url;
		link.download = fileName + fileExtension;
		link.click();
		window.URL.revokeObjectURL(url);
	};

	const mapDaysPeriod = (daysPeriod: string) => {
		switch (daysPeriod) {
			case DaysPerSimulationPeriod.BIWEEKLY:
				return 'Quincenal';
			case DaysPerSimulationPeriod.WEEKLY:
				return 'Semanal';
			case DaysPerSimulationPeriod.MONTHLY:
				return 'Mensual';
		}
	};

	const handlePrint = () => {
		if (!data) return;

		const printWindow = window.open('', '_blank');
		if (!printWindow) return;

		const tableContent = `
				<table>
					<thead>
						<tr>
							<th>Periodo</th>
							<th>Vence</th>
							<th>Sado inicial</th>
							<th>Pago crédito</th>
							<th>Abono a capital</th>
							<th>Interés</th>
							<th>Saldo Insoluto</th>
							<th>IVA</th>
							<th>GPS</th>
							<th>IVA GPS</th>
							<th>Seguro</th>
							<th>IVA Seguro</th>
							<th>Total</th>
						</tr>
					</thead>
					<tbody>
						${(data?.installments ?? [])
							.map(
								(row) => `
							<tr>
								<td>${row?.paymentNumber}</td>
								<td>${row?.dueDate}</td>
								<td>${row?.initialBalance}</td>
								<td>${row?.creditPayment}</td>
								<td>${row?.principalPayment}</td>
								<td>${row?.interestPayment ? `${row.interestPayment}` : '-'}</td>
								<td>${row?.outstandingBalance}</td>
								<td>${row?.interestTax || '-'}</td>
								<td>${row?.gpsAmount}</td>
								<td>${row?.gpsTax}</td>
								<td>${row?.insuranceAmount}</td>
								<td>${row?.insuranceTax}</td>
								<td>${row?.totalPayment}</td>
							</tr>
						`
							)
							.join('')}
					</tbody>
				</table>
			`;

		const printContent = `
				<html>
					<head>
						<title>Tabla de Amortización</title>
						<style>
							body { 
								font-family: Arial, sans-serif; 
								margin: 0;
								padding: 20px;
							}
							.container { 
								max-width: 1200px; 
								margin: 0 auto; 
							}
							.header { 
								text-align: center;
								margin-bottom: 30px;
								padding-bottom: 20px;
								border-bottom: 2px solid #eee;
							}
							.header h2 { 
								margin: 0;
								color: #333;
								font-size: 24px;
							}
							.header p { 
								margin: 10px 0 0 0;
								color: #666;
							}
							.loan-details {
								margin-bottom: 30px;
								padding: 15px;
								background: #f8f9fa;
								border-radius: 8px;
							}
							.loan-details div {
								margin: 8px 0;
								color: #444;
							}
							table { 
								width: 100%; 
								border-collapse: collapse; 
								margin: 20px 0;
								font-size: 14px;
							}
							th, td { 
								border: 1px solid #dee2e6; 
								padding: 12px 8px; 
								text-align: right;
							}
							th:first-child, td:first-child,
							th:nth-child(2), td:nth-child(2) { 
								text-align: left;
							}
							th { 
								background-color: #f8f9fa;
								font-weight: bold;
								color: #495057;
							}
							tr:nth-child(even) {
								background-color: #f8f9fa;
							}
							@media print {
								@page { margin: 2cm; }
								body { 
									print-color-adjust: exact;
									-webkit-print-color-adjust: exact;
								}
								th { background-color: #f8f9fa !important; }
								tr:nth-child(even) { background-color: #f8f9fa !important; }
							}
						</style>
					</head>
					<body>
						<div class="container">
							<div class="header">
								<h2>Tabla de Amortización</h2>
								<p>Fecha de generación: ${new Date().toLocaleDateString('es-MX')}</p>
							</div>
							<div class="loan-details">
								<div><strong>Plazo:</strong> ${data.numberOfPeriods} períodos</div>
								<div><strong>Periodicidad de Pago:</strong> ${data.daysPerPeriod}</div>
								<div><strong>Capacidad de Pago:</strong> ${data.paymentCapacity}</div>
								<div><strong>Monto Solicitado:</strong> ${data.configuration?.amountRequested}</div>
								<div><strong>Ultimo pago:</strong> ${data?.lastPaymentAmount}</div>
							</div>
							${tableContent}
						</div>
					</body>
				</html>
			`;

		printWindow.document.write(printContent);
		printWindow.document.close();

		setTimeout(() => {
			printWindow.print();
		}, 250);
	};

	if (loading) {
		return (
			<div className="flex flex-col items-center justify-center py-8 text-center">
				<img src="/loading.svg" />
				<Typography size="sm" color="#8C8C8C">
					{t('Loading')}
				</Typography>
			</div>
		);
	}

	if (!data || !data.installments || data.installments.length === 0) {
		return (
			<div className="flex flex-col items-center justify-center py-8 text-center">
				<img src="/noData.svg" />
				<Typography size="sm" color={GRAY_COLORS.gray500}>
					{t('CreateSimulationToViewTable')}
				</Typography>
			</div>
		);
	}

	return (
		<div className="p-6">
			<div className="flex flex-col gap-4 p-4 bg-white rounded-lg">
				<div className="flex flex-row items-center mb-4">
					<div className="grow ml-3">
						<Typography weight="semibold">{t('AmortizationTable')}</Typography>
					</div>
					<div className={`flex flex-row gap-2 items-center ${inCreditCover ? 'hidden' : ''}`}>
						{data?.installments && (
							<Button
								label={t('Print')}
								size="small"
								onClick={handlePrint}
								startIcon={<Printer />}
								color="gray"
							/>
						)}
						{data?.installments && (
							<Button
								label={t('ExportXls')}
								size="small"
								onClick={() =>
									handleExport(data, `simulacion_${new Date().toISOString().split('T')[0]}`)
								}
								startIcon={<img src="/excel-file.svg" width={24} height={24} />}
								color="success"
							/>
						)}
					</div>
				</div>
				<div className="overflow-x-auto flex flex-col justify-center items-center">
					<Table
						columns={columns}
						data={data?.installments || []}
						maxHeight="400px"
						className="overflow-x-auto max-w-[900px] xl:max-w-[1100px] 2xl:max-w-[1500px]"
					/>
				</div>
			</div>
		</div>
	);
};

export default SimulationTable;
